import type { NodeRef, RepertoireLine } from '../api/types';

/**
 * §6.2 — the UI keeps two DISTINCT concepts and must not conflate them:
 *
 *  - the **current canonical node** (`NodeRef`): source of the board position
 *    and its outgoing repertoire edges; and
 *  - the **current path context** (`lineId`, `ply`): source of move numbers,
 *    breadcrumbs, and back navigation.
 *
 * Back navigation follows the selected path, not an arbitrary parent of a
 * multi-parent node — conflating the two is the exact transposition bug the
 * product exists to prevent.
 */

/** Where the user is on one specific line. `ply` indexes `RepertoireLine.nodes`. */
export interface PathContext {
  lineId: string;
  /** 0-based index into `line.nodes` (root position = ply 0). */
  ply: number;
}

export interface NavigationState {
  node: NodeRef | null;
  path: PathContext | null;
}

export const EMPTY_NAVIGATION: NavigationState = { node: null, path: null };

/** Structural node identity: two NodeRefs are equal iff hash+id agree. */
export function sameNode(a: NodeRef | null, b: NodeRef | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.hashVersion === b.hashVersion &&
    a.zobristHash === b.zobristHash &&
    a.positionId === b.positionId
  );
}

/** The canonical node at a given ply of a line, or null when out of range. */
export function nodeAtPly(line: RepertoireLine, ply: number): NodeRef | null {
  return line.nodes[ply] ?? null;
}

/** Number of positions in a line (root + one per move). */
export function plyCount(line: RepertoireLine): number {
  return line.nodes.length;
}

export function canGoBack(state: NavigationState): boolean {
  return state.path !== null && state.path.ply > 0;
}

export function canGoForward(state: NavigationState, line: RepertoireLine | null): boolean {
  if (state.path === null || line === null) return false;
  return state.path.ply < line.nodes.length - 1;
}

/** Back navigation: step to the previous ply ON THE SELECTED PATH. */
export function back(state: NavigationState, line: RepertoireLine | null): NavigationState {
  if (state.path === null || line === null) return state;
  const ply = state.path.ply - 1;
  const node = nodeAtPly(line, ply);
  if (ply < 0 || node === null) return state;
  return { node, path: { lineId: state.path.lineId, ply } };
}

/** Forward navigation: step to the next ply ON THE SELECTED PATH. */
export function forward(state: NavigationState, line: RepertoireLine): NavigationState {
  if (state.path === null) return state;
  const ply = state.path.ply + 1;
  const node = nodeAtPly(line, ply);
  if (node === null) return state;
  return { node, path: { lineId: state.path.lineId, ply } };
}

/** Enter a path at a given ply, resolving the canonical node from the line. */
export function followPath(line: RepertoireLine, ply: number): NavigationState {
  const node = nodeAtPly(line, ply);
  return { node, path: { lineId: line.lineId, ply } };
}

/** Show a canonical node with no path context (e.g. a bare node link). */
export function goToNode(node: NodeRef): NavigationState {
  return { node, path: null };
}

/**
 * Apply a board move along an outgoing edge. The canonical node advances to the
 * edge target; the path context is preserved ONLY when the move continues the
 * selected line — otherwise the user has branched off and the path is cleared
 * (the transposition panel then offers the alternate incoming paths).
 */
export function applyMove(
  state: NavigationState,
  line: RepertoireLine | null,
  target: NodeRef,
): NavigationState {
  if (line !== null && state.path !== null && line.lineId === state.path.lineId) {
    const next = nodeAtPly(line, state.path.ply + 1);
    if (next !== null && sameNode(next, target)) {
      return { node: target, path: { lineId: line.lineId, ply: state.path.ply + 1 } };
    }
  }
  return { node: target, path: null };
}

export interface NodeOccurrence {
  lineId: string;
  /** ply index where this line reaches the node. */
  ply: number;
}

/** All (lineId, ply) occurrences of a node across a set of lines. */
export function findOccurrences(node: NodeRef, lines: RepertoireLine[]): NodeOccurrence[] {
  const occurrences: NodeOccurrence[] = [];
  for (const line of lines) {
    for (let ply = 0; ply < line.nodes.length; ply += 1) {
      if (sameNode(line.nodes[ply], node)) {
        occurrences.push({ lineId: line.lineId, ply });
      }
    }
  }
  return occurrences;
}
